import styled, { Interpolation, keyframes } from 'styled-components';

type props = {
  styles?: Interpolation<React.CSSProperties>
}

const LoaderDiv = styled.div<props>`
display: flex;
flex-direction:column;
width: 100%;
height: 100%;
align-items: center;
justify-content: center;
padding-inline: 10px;	
// min-height: 100vh;
img{margin-top: 5px;}

${({ styles }) => styles}
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.5);
    opacity: 0.6;
  }
`;

const PulseLoaderDiv = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 5px; /* Adds spacing between dots */
`;

const PulseDot = styled.div<{ delay: string }>`
  width: 15px;
  height: 15px;
  border-radius: 50%;
  background-color: #3498db; /* Change as needed */
  animation: ${pulseAnimation} 0.6s infinite alternate;
  animation-delay: ${(props) => props.delay}; /* Dynamic delay */
`;

// Radii (40/34/28) match the GIF's own measured scale within its 100x100 canvas — hole radius
// ~24, outer edge ~43 in the GIF, vs. this shape's hole radius 25 (28-3) and outer edge 43 (40+3).
const OUTER_CIRCUMFERENCE = 2 * Math.PI * 40;
const MIDDLE_CIRCUMFERENCE = 2 * Math.PI * 34;
const INNER_CIRCUMFERENCE = 2 * Math.PI * 28;

// Erase ends at -C, not +C: with stroke-dasharray fixed at C, the painted arc-length range is
// { s : (s + offset) mod 2C < C }. For offset decreasing from +C to 0, that range grows as
// [0, C-offset) — the far edge sweeps forward (counter-clockwise, given RING_TRANSFORM) while the
// near edge stays pinned at the anchor. Continuing offset past 0 down to -C (instead of reversing
// back up to +C) changes the range to [-offset, C) — now the near edge sweeps forward in that same
// direction while the far edge is the one pinned (same physical point, s≡0 mod C). The active edge
// never reverses either way — confirmed by tracking the GIF's arc across its full 81-frame cycle
// (frames 2-24: far edge fixed, near edge sweeps CCW to close the circle; frames 36-56: near edge
// fixed, far edge continues sweeping CCW to empty). Reversing back to +C instead retraces the same
// edge backward, reading as "breathing"/bidirectional rather than a continuous one-way sweep.
//
// Timing is a sequential relay, not three rings moving together: measured directly from the GIF
// by counting each color's visible arc-length frame-by-frame across its full 81-frame cycle.
// Outer starts growing immediately; inner doesn't start growing until outer has essentially just
// finished. None of the three grow together from 0%. Percentages are each ring's measured frame
// divided by 81 total frames:
//   outer:  grow 2→24, hold→34, shrink→58   (2%→30%→42%→72%)
//   middle: grow 12→33, hold→43, shrink→67  (15%→41%→53%→83%)
//   inner:  grow 25→46, hold→56, shrink→79  (31%→57%→69%→98%)
const drawOuterRing = keyframes`
  0%, 2% { stroke-dashoffset: ${OUTER_CIRCUMFERENCE}; }
  30% { stroke-dashoffset: 0; }
  42% { stroke-dashoffset: 0; }
  72%, 100% { stroke-dashoffset: ${-OUTER_CIRCUMFERENCE}; }
`;
const drawMiddleRing = keyframes`
  0%, 15% { stroke-dashoffset: ${MIDDLE_CIRCUMFERENCE}; }
  41% { stroke-dashoffset: 0; }
  53% { stroke-dashoffset: 0; }
  83%, 100% { stroke-dashoffset: ${-MIDDLE_CIRCUMFERENCE}; }
`;
const drawInnerRing = keyframes`
  0%, 31% { stroke-dashoffset: ${INNER_CIRCUMFERENCE}; }
  57% { stroke-dashoffset: 0; }
  69% { stroke-dashoffset: 0; }
  98%, 100% { stroke-dashoffset: ${-INNER_CIRCUMFERENCE}; }
`;

// 81 frames at the GIF's native 33.33fps = 2.43s exactly.
const GIF_CYCLE_DURATION = '2.43s';

const SpinnerOuterRing = styled.circle`
  stroke-dasharray: ${OUTER_CIRCUMFERENCE};
  animation: ${drawOuterRing} ${GIF_CYCLE_DURATION} linear infinite;
`;

const SpinnerMiddleRing = styled.circle`
  stroke-dasharray: ${MIDDLE_CIRCUMFERENCE};
  animation: ${drawMiddleRing} ${GIF_CYCLE_DURATION} linear infinite;
`;

const SpinnerInnerRing = styled.circle`
  stroke-dasharray: ${INNER_CIRCUMFERENCE};
  animation: ${drawInnerRing} ${GIF_CYCLE_DURATION} linear infinite;
`;

const LoaderBar = styled.div`
	 width: 220px;
	height: 6px; 
	background: var(--borderLightColor); 
	border-radius: 4px; 
	overflow: hidden;
	position: relative; 
`;

const LoaderProgress = styled.div`
	position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: linear-gradient(90deg, var(--primary), var(--secondary), var(--primary));
  background-size: 200% 100%;
  border-radius: 4px;
  transform-origin: left center; 
  animation: elasticMove 1.5s infinite ease-in-out;

  @keyframes elasticMove {
    0% {
      transform: translateX(-100%) scaleX(0.2);
    }
    50% {
      transform: translateX(20%) scaleX(0.6);
    }
    100% {
      transform: translateX(100%) scaleX(0.2);
    }
  }
`;

export {
	LoaderDiv,
	PulseDot,
	PulseLoaderDiv,
	LoaderBar,
	LoaderProgress,
	SpinnerOuterRing,
	SpinnerMiddleRing,
	SpinnerInnerRing
};